import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  UserPlus,
  X,
  Loader2,
  AlertCircle,
  CheckCircle2,
  User,
  Mail,
  Phone,
  // ShieldCheck, // Kept for future use
  // Eye, // Kept for future use
  // EyeOff, // Kept for future use
  AlertTriangle,
  Info
} from "lucide-react";
import { User as UserType } from "../../../types/user";
import { Role } from "../../../types/role";
import { roleService } from "../../../services/roleService";
import { userService } from "../../../services/userService";
import Select from "../../../shared/components/form/Select";
import Button from "../../../shared/components/ui/button/Button";

// Modern Clean Brand Colors - Subtle & Refined
const BRAND_COLORS = {
  primary: "#339274",          // Primary Green
  primaryHover: "#29795f",     // Darker Green for hover
  primaryLight: "#F8FCFA",     // Very subtle green background
  primarySoft: "#EDF7F2",      // Soft green background
  accent: "#A3CD39",           // Lime accent
  dark: "#007F66",             // Deep teal
  success: "#10B981",
  successLight: "#F0FDF4",
  error: "#EF4444",
  errorLight: "#FEF2F2",
  warning: "#F59E0B",
  warningLight: "#FFFBEB",
};

type DepartmentKey = 'OMS' | 'AMS' | 'EPC';
const DEPARTMENTS: DepartmentKey[] = ['OMS', 'AMS', 'EPC'];

interface Props {
  onAdd: (userData: {
    name: string;
    email: string;
    mobile: string;
    role: string;
    approverUserId?: string;
    departmentApprovers?: Partial<Record<DepartmentKey, string>>;
    id?: string;
  }) => void;
  onClose?: () => void;
  editingUser?: UserType | null;
}

const schema = yup.object({
  name: yup.string()
    .required("Name is required")
    .min(2, "Name must be at least 2 characters")
    .max(50, "Name must be less than 50 characters"),
  email: yup.string()
    .email("Please enter a valid email address")
    .required("Email is required"),
  mobile: yup.string()
    .required("Mobile number is required")
    .matches(/^[0-9]{10}$/, "Please enter a valid 10-digit mobile number"),
  roles: yup.array().of(
    yup.object().shape({
      roleId: yup.string().required("Role is required"),
      department: yup.string().oneOf(['OMS', 'AMS', 'EPC']).required("Department is required")
    })
  ).min(1, "At least one role must be assigned"),
  departmentApprovers: yup.object().shape({
    OMS: yup.string().optional(),
    AMS: yup.string().optional(),
    EPC: yup.string().optional(),
  }).optional(),
});

type FormValues = yup.InferType<typeof schema>;

export default function UserForm({ onAdd, onClose, editingUser }: Props) {
  const [isLoadingRoles, setIsLoadingRoles] = useState<boolean>(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const [rolesLoaded, setRolesLoaded] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // Approver users keyed by department; each list only contains approvers for that department.
  const [approversByDept, setApproversByDept] = useState<Record<DepartmentKey, UserType[]>>({
    OMS: [],
    AMS: [],
    EPC: [],
  });
  const [loadingApproverDepts, setLoadingApproverDepts] = useState<Record<DepartmentKey, boolean>>({
    OMS: false,
    AMS: false,
    EPC: false,
  });

  // Department-wise role selection state
  const [departmentRoles, setDepartmentRoles] = useState<{
    OMS?: string;
    AMS?: string;
    EPC?: string;
  }>({});

  // Generate role options from roles
  const roleOptions = roles.map(role => ({
    value: role.id || role._id || "",
    label: role.name,
    department: role.department || 'OMS'
  }));

  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting, isDirty, isValid },
    setFocus,
    getValues,
    setValue
  } = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    mode: "onChange",
    defaultValues: {
      roles: []
    }
  });

  const watchedName = watch("name");
  const watchedEmail = watch("email");
  const watchedMobile = watch("mobile");
  const watchedRoles = watch("roles");
  const watchedDeptApprovers = watch("departmentApprovers");

  // For each department, determine if the assigned role is one that requires an approver.
  // Mobile-user roles never need one — their approvals are handled dynamically
  // per project, not through the role-level approver mapping.
  const departmentNeedsApprover = (dept: DepartmentKey): boolean => {
    const roleId = departmentRoles[dept];
    if (!roleId) return false;
    const role = roles.find(r => (r.id || r._id) === roleId);
    if (!role || role.isMobileUserRole) return false;
    return role.roleType === 'requester' || role.roleType === 'both';
  };

  const departmentsRequiringApprover: DepartmentKey[] = DEPARTMENTS.filter(departmentNeedsApprover);

  // Watch for role changes
  useEffect(() => {
    console.log("Roles field value changed:", watchedRoles);
  }, [watchedRoles]);

  // Watch for role field value changes specifically for debugging
  useEffect(() => {
    if (editingUser && watchedRoles) {
      console.log("Roles field value changed for editing user:", {
        rolesValue: watchedRoles,
        editingUser: editingUser.id || editingUser._id,
        availableRoles: roles.length
      });
    }
  }, [watchedRoles, editingUser, roles]);

  // Clear invalid role when editing user and role not found
  /* useEffect(() => {
    if (editingUser && rolesLoaded && roles.length > 0 && watchedRole) {
      const roleExists = roles.some(role => (role.id || role._id) === watchedRole);
      if (!roleExists) {
        console.warn("Clearing invalid role value:", watchedRole);
        // Clear the invalid role value
        setValue("role", "");
      }
    }
  }, [editingUser, roles, rolesLoaded, watchedRole, setValue]); */

  // Fetch roles
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        setIsLoadingRoles(true);
        const rolesData = await roleService.getAllRolesForDDL();
        // Since getAllRolesForDDL returns an array directly, we can use it as is
        setRoles(rolesData.filter((role: Role) => role.isActive));
        setRolesLoaded(true);
      } catch (error) {
        console.error("Failed to fetch roles:", error);
        toast.error("Failed to load roles");
      } finally {
        setIsLoadingRoles(false);
      }
    };

    fetchRoles();
  }, []);

  // Fetch approver candidates for each department whose assigned role needs an approver.
  // Cleared for departments that no longer need an approver.
  useEffect(() => {
    if (!rolesLoaded) return;

    DEPARTMENTS.forEach((dept) => {
      const needsApprover = departmentNeedsApprover(dept);
      if (!needsApprover) {
        setApproversByDept((prev) => (prev[dept].length === 0 ? prev : { ...prev, [dept]: [] }));
        // Clear any previously-chosen approver for a department that no longer needs one.
        if (watchedDeptApprovers?.[dept]) {
          setValue(`departmentApprovers.${dept}` as const, "", { shouldValidate: true, shouldDirty: true });
        }
        return;
      }

      // Avoid refetching if we already have candidates loaded for this department.
      if (approversByDept[dept].length > 0 || loadingApproverDepts[dept]) return;

      (async () => {
        try {
          setLoadingApproverDepts((prev) => ({ ...prev, [dept]: true }));
          const approvers = await userService.getApproverUsers(dept);
          setApproversByDept((prev) => ({ ...prev, [dept]: approvers }));
        } catch (error) {
          console.error(`Failed to fetch approver users for ${dept}:`, error);
          toast.error(`Failed to load ${dept} approvers`);
        } finally {
          setLoadingApproverDepts((prev) => ({ ...prev, [dept]: false }));
        }
      })();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentRoles, roles, rolesLoaded]);

  // Auto-fill form if editing
  useEffect(() => {
    if (editingUser && rolesLoaded && roles.length > 0) {
      // Extract roles
      const userRoles: any[] = [];
      
      // Handle legacy single role
      if (editingUser.role) {
        let roleId = "";
        if (typeof editingUser.role === "string") {
          roleId = editingUser.role;
        } else if (editingUser.role._id) {
          roleId = editingUser.role._id;
        } else if (editingUser.role.id) {
          roleId = editingUser.role.id;
        }
        
        if (roleId) {
          // Find the role to get its department
          const role = roles.find(r => (r.id || r._id) === roleId);
          if (role) {
            userRoles.push({
              roleId: roleId,
              department: role.department || 'OMS'
            });
          }
        }
      }
      
      // Handle new roles array if available (future proofing)
      if (editingUser.roles && Array.isArray(editingUser.roles)) {
        editingUser.roles.forEach(r => {
          // Check if not already added
          if (!userRoles.some(ur => ur.roleId === (typeof r.roleId === 'string' ? r.roleId : r.roleId._id || r.roleId.id))) {
             userRoles.push({
               roleId: typeof r.roleId === 'string' ? r.roleId : r.roleId._id || r.roleId.id,
               department: r.department
             });
          }
        });
      }

      // Extract per-department approvers, falling back to legacy approverUserId for OMS if present.
      const departmentApprovers: Partial<Record<DepartmentKey, string>> = {
        OMS: editingUser.departmentApprovers?.OMS || "",
        AMS: editingUser.departmentApprovers?.AMS || "",
        EPC: editingUser.departmentApprovers?.EPC || "",
      };
      if (!editingUser.departmentApprovers && editingUser.approverUserId) {
        const legacyDept = userRoles.find(r => {
          const role = roles.find(rr => (rr.id || rr._id) === r.roleId);
          if (!role || role.isMobileUserRole) return false;
          return role.roleType === 'requester' || role.roleType === 'both';
        })?.department as DepartmentKey | undefined;
        if (legacyDept) departmentApprovers[legacyDept] = editingUser.approverUserId;
      }

      // Update local state for department roles
      const deptRoles: any = {};
      userRoles.forEach(r => {
        deptRoles[r.department] = r.roleId;
      });
      setDepartmentRoles(deptRoles);

      // Use a more robust approach to ensure form reset happens correctly
      const resetForm = () => {
        const formData = {
          name: editingUser.name,
          email: editingUser.email,
          mobile: editingUser.mobile,
          roles: userRoles,
          departmentApprovers,
        };

        reset(formData);
      };

      // Execute the reset
      resetForm();
    } else if (!editingUser) {
      reset({
        roles: [],
        departmentApprovers: { OMS: "", AMS: "", EPC: "" },
      });
      setDepartmentRoles({});
    }
    if (editingUser) {
      setFocus("name");
    }
  }, [editingUser, reset, setFocus, roles, rolesLoaded]);

  // Handle department role selection
  const handleRoleSelect = (department: string, roleId: string) => {
    const newDeptRoles = { ...departmentRoles, [department]: roleId };
    
    // If roleId is empty, remove the key
    if (!roleId) {
      delete (newDeptRoles as any)[department];
    }
    
    setDepartmentRoles(newDeptRoles);
    
    // Update form value
    const rolesArray = Object.entries(newDeptRoles).map(([dept, rId]) => ({
      roleId: rId as string,
      department: dept as 'OMS' | 'AMS' | 'EPC'
    }));
    
    setValue('roles', rolesArray, { shouldValidate: true, shouldDirty: true });
  };

  const onSubmit = async (data: FormValues) => {
    setApiError(null);
    setShowSuccess(false);

    // Additional validation: ensure at least one role is selected
    if (!data.roles || data.roles.length === 0) {
      setApiError("Please select at least one role for the user.");
      return;
    }

    // Validate that every department whose role requires an approver has one selected.
    const missingApproverDept = departmentsRequiringApprover.find(
      (dept) => !data.departmentApprovers?.[dept]
    );
    if (missingApproverDept) {
      setApiError(`Please assign an approver for the ${missingApproverDept} department.`);
      return;
    }

    try {
      // Prepare the data to pass to parent component
      // For backward compatibility, set 'role' to the first selected role
      // The backend should handle 'roles' array
      const primaryRole = data.roles[0];

      // Strip empty strings from the department-wise approvers map.
      const departmentApprovers: Partial<Record<DepartmentKey, string>> = {};
      DEPARTMENTS.forEach((dept) => {
        const v = data.departmentApprovers?.[dept];
        if (v) departmentApprovers[dept] = v;
      });

      // Backward-compatible legacy primary approver: pick the first department with one.
      const legacyApproverUserId = departmentsRequiringApprover
        .map((d) => departmentApprovers[d])
        .find(Boolean);

      const userData = {
        name: data.name,
        email: data.email,
        mobile: data.mobile,
        role: primaryRole.roleId, // Primary role for backward compatibility
        roles: data.roles, // New multi-role array
        departmentApprovers,
        approverUserId: legacyApproverUserId || undefined,
        // Include the user ID if editing
        ...(editingUser && { id: editingUser.id || editingUser._id })
      };

      // Call the parent component's handler and wait for it to complete
      // The parent component (Users.tsx) will handle the actual API calls
      await onAdd(userData as any);

      // Show success message only if API call succeeded
      setShowSuccess(true);
      toast.success(
        editingUser
          ? "User updated successfully and link sent to registered email!"
          : "User created successfully and link shared via email!"
      );

      // Close form after a short delay to show success message
      setTimeout(() => {
        onClose?.();
        reset();
      }, 1000);
    } catch (err) {
      const error = err as AxiosError<{ message?: string }>;
      let message = error.response?.data?.message || error.message || "Something went wrong. Please try again.";

      // Handle specific validation errors with user-friendly messages
      // For validation errors, show the exact backend message as it contains detailed format information
      if (message.includes("status") && message.includes("not a valid enum value")) {
        message = "There's a temporary issue with user status validation. Please try again or contact support if the issue persists.";
      } else if (message.includes("Email linked to another account") || message.includes("email") && message.includes("already")) {
        message = "Email address already exists. Please use a different email address.";
      } else if (message.includes("Mobile already linked") || message.includes("mobile") && message.includes("already")) {
        message = "Mobile number already exists. Please use a different mobile number.";
      } else if (message.includes("duplicate")) {
        if (message.toLowerCase().includes("email")) {
          message = "Email address already exists. Please use a different email address.";
        } else if (message.toLowerCase().includes("mobile")) {
          message = "Mobile number already exists. Please use a different mobile number.";
        }
      }
      // Note: Removed generic "User validation failed" handler to show detailed backend validation messages

      setApiError(message);
      console.error("User Submit Error:", error);
      // Don't reset the form - keep the entered data so user can fix the issue
    }
  };

  // Debug logging for role options and current form values
  useEffect(() => {
    if (editingUser && rolesLoaded && roles.length > 0) {
      console.log("Role options generated:", roles.map(role => ({
        value: role.id || role._id || "",
        label: role.name
      })));
      console.log("Current form values:", getValues());
      console.log("Editing user role:", editingUser.role);
      console.log("Available roles:", roles);
    }
  }, [editingUser, roles, rolesLoaded, getValues]);

  const formTitle = editingUser ? "Edit User" : "Add New User";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      {/* Success Message - Clean Style */}
      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-3 p-4 rounded-lg border"
            style={{
              backgroundColor: BRAND_COLORS.successLight,
              borderColor: `${BRAND_COLORS.success}40`
            }}
          >
            <div className="w-10 h-10 rounded-lg flex items-center justify-center border"
              style={{
                backgroundColor: BRAND_COLORS.success + '20',
                borderColor: BRAND_COLORS.success + '40'
              }}
            >
              <CheckCircle2 className="w-5 h-5" style={{ color: BRAND_COLORS.success }} />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold" style={{ color: BRAND_COLORS.success }}>
                {editingUser ? "User updated successfully!" : "User created successfully!"}
              </p>
              <p className="text-xs mt-1" style={{ color: BRAND_COLORS.success }}>
                Credentials have been sent to the user's email address.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error Alert - Clean Style */}
      <AnimatePresence>
        {apiError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-3 p-4 rounded-lg border"
            style={{
              backgroundColor: BRAND_COLORS.errorLight,
              borderColor: `${BRAND_COLORS.error}40`
            }}
          >
            <div className="w-10 h-10 rounded-lg flex items-center justify-center border"
              style={{
                backgroundColor: BRAND_COLORS.error + '20',
                borderColor: BRAND_COLORS.error + '40'
              }}
            >
              <AlertTriangle className="w-5 h-5" style={{ color: BRAND_COLORS.error }} />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold" style={{ color: BRAND_COLORS.error }}>Error</p>
              <p className="text-xs mt-1" style={{ color: BRAND_COLORS.error }}>{apiError}</p>
            </div>
            <button
              onClick={() => setApiError(null)}
              className="p-1.5 rounded-lg hover:bg-red-100 transition-colors"
              style={{ color: BRAND_COLORS.error }}
            >
              <X size={16} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Form Header - Clean Design */}
      <div className="flex items-center gap-3 pb-5 border-b border-gray-200">
        <div className="w-12 h-12 rounded-lg flex items-center justify-center border"
          style={{
            background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})`,
            borderColor: BRAND_COLORS.primary
          }}
        >
          <UserPlus className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-semibold text-gray-900">{formTitle}</h2>
          <p className="text-sm text-gray-600 mt-1 leading-relaxed">
            {editingUser
              ? "Update user information and role assignments"
              : "Create a new user account with role-based access control"
            }
          </p>
          {/* Warning for no roles assigned */}
          {editingUser && rolesLoaded && watchedRoles && watchedRoles.length === 0 && (
            <div className="flex items-center gap-2 mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle size={14} className="text-amber-600" />
              <span className="text-xs text-amber-700 font-medium">
                No roles assigned. Please select at least one role.
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Form Body - Clean Layout */}
      <form
        key={editingUser?.id || editingUser?._id || 'new-user'}
        onSubmit={handleSubmit(onSubmit as any)}
        className="space-y-5"
      >
        {/* Name Field - Clean Style */}
        <div className="space-y-2">
          <label
            htmlFor="name"
            className="block text-sm font-medium text-gray-900"
          >
            Full Name *
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <User size={16} className="text-gray-500" />
            </div>
            <input
              id="name"
              type="text"
              {...register("name")}
              className={`h-10 w-full rounded-lg border bg-white pl-9 pr-10 py-3 text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${errors.name
                ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                : watchedName && !errors.name
                  ? `focus:ring-opacity-20`
                  : "border-gray-300 hover:border-gray-400"
                }`}
              style={watchedName && !errors.name ? {
                borderColor: BRAND_COLORS.primary,
                '--tw-ring-color': BRAND_COLORS.primary + '40'
              } as React.CSSProperties : !errors.name ? {
                '--tw-ring-color': BRAND_COLORS.primary + '40'
              } as React.CSSProperties : {}}
              placeholder="Enter full name"
            />
            {watchedName && !errors.name && (
              <CheckCircle2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
                style={{ color: BRAND_COLORS.primary }} />
            )}
            {errors.name && (
              <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-red-500" />
            )}
          </div>
          {errors.name && (
            <p className="flex items-center gap-1.5 text-xs text-red-600">
              <AlertCircle className="w-3 h-3 flex-shrink-0" />
              {errors.name.message}
            </p>
          )}
          {watchedName && !errors.name && (
            <p className="flex items-center gap-1.5 text-xs" style={{ color: BRAND_COLORS.primary }}>
              <CheckCircle2 className="w-3 h-3 flex-shrink-0" />
              Name looks good!
            </p>
          )}
        </div>

        {/* Email Field - Clean Style */}
        <div className="space-y-2">
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-900"
          >
            Email Address *
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Mail size={16} className="text-gray-500" />
            </div>
            <input
              id="email"
              type="email"
              {...register("email")}
              className={`h-10 w-full rounded-lg border bg-white pl-9 pr-10 py-3 text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${errors.email
                ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                : watchedEmail && !errors.email
                  ? `focus:ring-opacity-20`
                  : "border-gray-300 hover:border-gray-400"
                }`}
              style={watchedEmail && !errors.email ? {
                borderColor: BRAND_COLORS.primary,
                '--tw-ring-color': BRAND_COLORS.primary + '40'
              } as React.CSSProperties : !errors.email ? {
                '--tw-ring-color': BRAND_COLORS.primary + '40'
              } as React.CSSProperties : {}}
              placeholder="Enter email address"
            />
            {watchedEmail && !errors.email && (
              <CheckCircle2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
                style={{ color: BRAND_COLORS.primary }} />
            )}
            {errors.email && (
              <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-red-500" />
            )}
          </div>
          {errors.email && (
            <p className="flex items-center gap-1.5 text-xs text-red-600">
              <AlertCircle className="w-3 h-3 flex-shrink-0" />
              {errors.email.message}
            </p>
          )}
          {watchedEmail && !errors.email && (
            <p className="flex items-center gap-1.5 text-xs" style={{ color: BRAND_COLORS.primary }}>
              <CheckCircle2 className="w-3 h-3 flex-shrink-0" />
              Email format is valid!
            </p>
          )}
        </div>

        {/* Mobile Field - Clean Style */}
        <div className="space-y-2">
          <label
            htmlFor="mobile"
            className="block text-sm font-medium text-gray-900"
          >
            Mobile Number *
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Phone size={16} className="text-gray-500" />
            </div>
            <input
              id="mobile"
              type="tel"
              {...register("mobile")}
              className={`h-10 w-full rounded-lg border bg-white pl-9 pr-10 py-3 text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${errors.mobile
                ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                : watchedMobile && !errors.mobile
                  ? `focus:ring-opacity-20`
                  : "border-gray-300 hover:border-gray-400"
                }`}
              style={watchedMobile && !errors.mobile ? {
                borderColor: BRAND_COLORS.primary,
                '--tw-ring-color': BRAND_COLORS.primary + '40'
              } as React.CSSProperties : !errors.mobile ? {
                '--tw-ring-color': BRAND_COLORS.primary + '40'
              } as React.CSSProperties : {}}
              placeholder="Enter 10-digit mobile number"
            />
            {watchedMobile && !errors.mobile && (
              <CheckCircle2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
                style={{ color: BRAND_COLORS.primary }} />
            )}
            {errors.mobile && (
              <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-red-500" />
            )}
          </div>
          {errors.mobile && (
            <p className="flex items-center gap-1.5 text-xs text-red-600">
              <AlertCircle className="w-3 h-3 flex-shrink-0" />
              {errors.mobile.message}
            </p>
          )}
          {watchedMobile && !errors.mobile && (
            <p className="flex items-center gap-1.5 text-xs" style={{ color: BRAND_COLORS.primary }}>
              <CheckCircle2 className="w-3 h-3 flex-shrink-0" />
              Mobile number is valid!
            </p>
          )}
        </div>

        {/* Role Assignment Section - Clean Style */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-900">
            Assign Roles by Department *
          </label>
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-4">
            {['OMS', 'AMS', 'EPC'].map((dept) => {
              // Filter roles for this department
              const deptRoles = roles.filter(r => (r.department || 'OMS') === dept);
              const deptOptions = deptRoles.map(r => ({
                value: r.id || r._id || "",
                label: r.name
              }));
              
              return (
                <div key={dept} className="grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-3">
                    <span className="text-sm font-medium text-gray-700">{dept}</span>
                  </div>
                  <div className="col-span-9">
                    <Select
                      options={deptOptions}
                      value={deptOptions.find(opt => opt.value === departmentRoles[dept as keyof typeof departmentRoles])}
                      onChange={(option) => handleRoleSelect(dept, option?.value || "")}
                      placeholder={`Select ${dept} Role (Optional)`}
                      className="h-10"
                      isDisabled={isLoadingRoles}

                    />
                  </div>
                </div>
              );
            })}
          </div>
          
          {errors.roles && (
            <p className="flex items-center gap-1.5 text-xs text-red-600">
              <AlertCircle className="w-3 h-3 flex-shrink-0" />
              {typeof errors.roles.message === 'string' ? errors.roles.message : "At least one role must be assigned"}
            </p>
          )}
        </div>

        {/* Department-wise Approver Selection - shown only for departments where the role is requester/both */}
        {departmentsRequiringApprover.length > 0 && (
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-900">
              Assign Approver by Department *
            </label>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-4">
              {departmentsRequiringApprover.map((dept) => {
                const candidates = approversByDept[dept];
                const isLoading = loadingApproverDepts[dept];
                const approverOptions = candidates.map((user) => ({
                  value: user._id || user.id || "",
                  label: `${user.name}${user.email ? ` (${user.email})` : ''}`,
                }));

                return (
                  <div key={dept} className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-3">
                      <span className="text-sm font-medium text-gray-700">{dept}</span>
                    </div>
                    <div className="col-span-9">
                      <Controller
                        name={`departmentApprovers.${dept}` as const}
                        control={control}
                        render={({ field }) => {
                          const selected = approverOptions.find((opt) => opt.value === field.value);
                          return (
                            <Select
                              options={approverOptions}
                              value={selected}
                              onChange={(option) => field.onChange(option?.value || "")}
                              placeholder={
                                isLoading
                                  ? `Loading ${dept} approvers...`
                                  : approverOptions.length === 0
                                    ? `No ${dept} approvers available`
                                    : `Select ${dept} approver`
                              }
                              className="h-10"
                              isDisabled={isLoading || approverOptions.length === 0}
                            />
                          );
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="flex items-center gap-1.5 text-xs text-gray-500">
              <Info className="w-3 h-3 flex-shrink-0" />
              An approver is required for each department where the user holds a requester role.
            </p>
          </div>
        )}

        {/* Information Note - Clean Style */}
        <div className="p-4 rounded-lg border"
          style={{
            backgroundColor: BRAND_COLORS.primaryLight,
            borderColor: BRAND_COLORS.primary + '30'
          }}
        >
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center border"
              style={{
                backgroundColor: BRAND_COLORS.primary + '20',
                borderColor: BRAND_COLORS.primary + '40'
              }}
            >
              <Info size={16} style={{ color: BRAND_COLORS.primary }} />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium" style={{ color: BRAND_COLORS.dark }}>
                Account Setup Information
              </p>
              <p className="text-xs text-gray-700 mt-1 leading-relaxed">
                {editingUser
                  ? "User will receive an email with updated credentials and login instructions. Their access permissions will be updated based on the selected role."
                  : "User will receive an email with secure login credentials and comprehensive setup instructions. Access will be granted based on the assigned role permissions."
                }
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons - Sticky Footer */}
        <div className="sticky bottom-0 flex flex-col sm:flex-row gap-3 pt-5 pb-2 border-t border-gray-200 bg-white z-10">
          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={() => {
              onClose?.();
            }}
            disabled={isSubmitting}
            className="w-full sm:w-auto order-2 sm:order-1 font-medium h-10 px-6 text-sm border rounded-lg"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="sorigin"
            size="lg"
            disabled={isSubmitting || !isDirty || !isValid || isLoadingRoles || !!(editingUser && !rolesLoaded)}
            className="w-full sm:w-auto min-w-[140px] order-1 sm:order-2 font-medium h-10 px-6 text-sm rounded-lg"
          >
            {isSubmitting ? (
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                {editingUser ? "Updating..." : "Creating..."}
              </div>
            ) : editingUser ? (
              "Update User"
            ) : (
              "Create User"
            )}
          </Button>
        </div>
      </form>
    </motion.div>
  );
}
